import { tw } from "@careinfo/tailwind";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OutLineButton from "../../components/Reusable/OutlineButton";
import APIS from "../../constant/Endpoint";
import { DeleteIcon } from "../../Icons/DeleteIcon";
import Button from "../Reusable/Button";
import DataTable from "../Reusable/Datatable";
import TableSearch from "../Reusable/Datatable/components/Search";
import NACell from "../Reusable/Datatable/NACell";
import PopupModal from "../Reusable/Modal";
import SwitchButton from "../Reusable/SwitchButton";
import TableActionButton from "../Reusable/TableActionButton";
import ProductGroupForm from "./ProductGroup";
import { AccessControl } from "@careinfo/permission-required";

const InventoryInfo = ({
  handleDelete,
  isLoading,
  handleIsActive,
  handleIsMaintainStock,
  handleIsTaxable,
}) => {
  const [openCategoryFormPopup, setOpenCategoryFormPopup] = useState(false);

  const navigate = useNavigate();

  const columns: ColumnDef<any, any>[] = [
    {
      accessorKey: "hs_code",
      header: "HS Code",
      cell: NACell,
    },
    {
      accessorKey: "category.name",
      header: "Product Group",
      cell: NACell,
    },
    {
      accessorKey: "name",
      header: "Product Name",
      cell: ({ row, getValue }: any) => {
        return <p className={tw`w-60`}>{getValue()}</p>;
      },
    },
    {
      accessorKey: "unit.name",
      header: "Unit",
      cell: NACell,
    },
    {
      accessorKey: "opening_quantity",
      header: "Opening Quantity",
      cell: ({ getValue }: any) => <p>{getValue() ?? 0}</p>,
    },
    {
      accessorKey: "opening_rate",
      header: "Opening Rate",
      cell: ({ getValue }: any) => (
        <p>
          {getValue()
            ? getValue().toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : 0}
        </p>
      ),
    },
    {
      accessorKey: "opening_balance",
      header: "Opening Value",
      cell: ({ getValue }: any) => (
        <p>
          {getValue()
            ? getValue().toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : 0}
        </p>
      ),
    },
    {
      accessorKey: "is_maintain_stock",
      header: "Maintain Stock",
      cell: ({ row, getValue }: any) => (
        <SwitchButton
          id={row?.original?.id}
          value={getValue()}
          disabled={false}
          onChange={handleIsMaintainStock}
        />
      ),
    },
    {
      accessorKey: "is_taxable",
      header: "Taxable",
      cell: ({ row, getValue }: any) => (
        <SwitchButton
          id={row?.original?.id}
          value={getValue()}
          disabled={false}
          onChange={handleIsTaxable}
        />
      ),
    },
    {
      accessorKey: "is_active",
      header: "Active",
      cell: ({ row, getValue }: any) => (
        <SwitchButton
          id={row?.original?.id}
          value={getValue()}
          disabled={false}
          onChange={handleIsActive}
        />
      ),
    },
    {
      accessorKey: "id",
      header: "Action",
      cell: ({ row, getValue }: any) => {
        return (
          <div className={tw`grid grid-cols-2 gap-x-4 items-center`}>
            <AccessControl allowedPermissions={["can_edit_inventory"]}>
              <button type="button" className={tw`focus:outline-none`}>
                <TableActionButton editPath={`edit/${getValue()}`} />
              </button>
            </AccessControl>
            <AccessControl allowedPermissions={["can_delete_product"]}>
              <button
                type="button"
                className={tw`focus:outline-none w-6 h-6`}
                onClick={() => handleDelete(getValue())}
              >
                <DeleteIcon color={"var(--primary)"} />
              </button>
            </AccessControl>
          </div>
        );
      },
    },
  ];

  return (
    <div className={tw`space-y-4 mt-4`}>
      <div className={tw` flex gap-x-2`}>
        <AccessControl allowedPermissions={["can_search_product"]}>
          <TableSearch />
        </AccessControl>

        <div className={tw`w-32`}>
          <AccessControl allowedPermissions={["can_add_product"]}>
            <Button
              label="Add New Product"
              type="button"
              onClick={() => navigate("create")}
            />
          </AccessControl>
        </div>
        <AccessControl allowedPermissions={["can_view_product_group"]}>
          <OutLineButton
            onClick={() => setOpenCategoryFormPopup(!openCategoryFormPopup)}
            label="Add Product Group"
          />
        </AccessControl>
      </div>
      <DataTable
        columns={columns}
        queryKey={"inventory-info"}
        url={APIS.PRODUCT}
        params={{}}
        customSearch={true}
        tableName="inventory-info"
      />
      <PopupModal
        isOpen={openCategoryFormPopup}
        setIsOpen={setOpenCategoryFormPopup}
        title="Product Group"
      >
        <div className={tw`w-[400px]`}>
          <ProductGroupForm
            setOpenCategoryFormPopup={setOpenCategoryFormPopup}
          />
        </div>
      </PopupModal>
    </div>
  );
};

export default InventoryInfo;
